<template>
    <div class="row" v-if="local_step !== undefined && lang != undefined">
        <!-- KEY -->
        <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('form.key.label') }}*
                    <small class="d-block text-body-tertiary fw-normal">
                        {{ $t('feature.surveys.$steps.key_recommendations') }}
                    </small>
                </label>
                <input v-model="local_step.key" class="form-control shadow-none text-black" required
                    :class="[{ 'is-invalid': submitted && errors?.key }]" />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.key ?? false">
                    <p v-for="error in errors.key" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}
                    </p>
                </div>
            </div>
        </div>
        <div class="col-12"></div>
        <!-- TYPE -->
        <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('feature.surveys.$steps.type') }}*
                </label>
                <div>
                    <select class="form-select shadow-none text-black fs-md-15" v-model="local_step.type"
                        :class="[{ 'is-invalid': submitted && errors?.text }]" required :disabled="update">
                        <option v-for="key in typologies" :value="key" :key="key">
                            {{ $t(`feature.surveys.$steps.$type.${key}`) }}</option>
                    </select>
                    <!-- ERRORS -->
                    <div class="form-messages mt-2 px-1" v-if="errors?.type ?? false">
                        <p v-for="error in errors.type" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                            error }}</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-12"></div>

        <!-- TITLE -->
        <div class="col-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <div class="d-flex justify-content-between">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('form.title.label') }}*
                    </label>
                    <div class="icon-link transition mb-10 cursor-pointer text-uppercase" @click="toggleRawEdit">
                        <i class="ph ph-code" v-if="!raw_edit"></i>
                        {{ raw_edit ? $t('feature.contents.$blocks.editor') : $t('feature.contents.$blocks.raw') }}
                    </div>
                </div>
                <!-- RAW EDIT -->
                <textarea v-if="raw_edit" v-model="local_step.title![lang]" class="form-control shadow-none text-black"
                    rows="8" :class="[{ 'is-invalid': submitted && errors?.title }]" required></textarea>
                <div class="mb-0 quill-editor" v-else>
                    <QuillEditor theme="snow" :toolbar="toolbar" v-model:content="local_step.title![lang]" contentType="html"
                        :key="`${local_step.id}-title-${lang}-${changes}`" />
                </div>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.title ?? false">
                    <p v-for="error in errors.title" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}</p>
                </div>
            </div>
        </div>
        <!-- TEXT -->
        <div class="col-12" v-if="local_step.title !== undefined">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <div class="d-flex justify-content-between">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('form.subtitle.label') }}
                    </label>
                    <div class="icon-link transition mb-10 cursor-pointer text-uppercase" @click="toggleRawEdit">
                        <i class="ph ph-code" v-if="!raw_edit"></i>
                        {{ raw_edit ? $t('feature.contents.$blocks.editor') : $t('feature.contents.$blocks.raw') }}
                    </div>
                </div>
                <!-- RAW EDIT -->
                <textarea v-if="raw_edit" v-model="local_step.text![lang]" class="form-control shadow-none text-black"
                    rows="8" :class="[{ 'is-invalid': submitted && errors?.text }]" required></textarea>
                <div class="mb-0 quill-editor" v-else>
                    <QuillEditor theme="snow" v-model:content="local_step.text![lang]" contentType="html"
                        :key="`${local_step.id}-text-${lang}-${changes}`" />
                </div>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.text ?? false">
                    <p v-for="error in errors.text" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}</p>
                </div>
            </div>
        </div>
        <!-- FOOTER -->
        <div class="col-12" v-if="local_step.body !== undefined">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <div class="d-flex justify-content-between">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('form.body.label') }}
                    </label>
                    <div class="icon-link transition mb-10 cursor-pointer text-uppercase" @click="toggleRawEdit">
                        <i class="ph ph-code" v-if="!raw_edit"></i>
                        {{ raw_edit ? $t('feature.contents.$blocks.editor') : $t('feature.contents.$blocks.raw') }}
                    </div>
                </div>
                <!-- RAW EDIT -->
                <textarea v-if="raw_edit" v-model="local_step.body![lang]" class="form-control shadow-none text-black"
                    rows="8" :class="[{ 'is-invalid': submitted && errors?.body }]" required></textarea>
                <div class="mb-0 quill-editor" v-else>
                    <QuillEditor theme="snow" v-model:content="local_step.body![lang]" contentType="html"
                        :key="`${local_step.id}-body-${lang}-${changes}`" />
                </div>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.body ?? false">
                    <p v-for="error in errors.body" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}</p>
                </div>
            </div>
        </div>
        <template v-if="local_step.type === 'V'">
            <!-- VALUE REPRESANTION TYPE -->
            <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('feature.surveys.value_representation_type') }}*
                    </label>
                    <div>
                        <select class="form-select shadow-none text-black fs-md-15"
                            v-model="local_step.value_representation_type"
                            :class="[{ 'is-invalid': submitted && errors?.value_representation_type }]"
                            :required="local_step.type === 'V'" :disabled="update">
                            <option v-for="key in value_representation_types" :value="key" :key="key">
                                {{ $t(`feature.surveys.$value_representation_type.${key}`) }}</option>
                        </select>
                        <!-- ERRORS -->
                        <div class="form-messages mt-2 px-1" v-if="errors?.value_representation_type ?? false">
                            <p v-for="error in errors.value_representation_type" :key="error"
                                class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12"></div>
            <!-- MIN -->
            <div class="col-6 col-sm-4 col-md-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('feature.surveys.min_value') }}*
                    </label>
                    <input v-model="local_step.min_value" class="form-control shadow-none text-black"
                        :required="local_step.type === 'V'" type="number"
                        :class="[{ 'is-invalid': submitted && errors?.min_value }]" />
                    <!-- ERRORS -->
                    <div class="form-messages mt-2 px-1" v-if="errors?.min_value ?? false">
                        <p v-for="error in errors.min_value" :key="error" class="small text-danger lh-1 m-0 mt-1">
                            {{ error }}
                        </p>
                    </div>
                </div>
            </div>
            <!-- MIN LABEL-->
            <div class="col-sm-8 col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('feature.surveys.min_label') }}*
                    </label>
                    <input v-model="local_step.min_label" class="form-control shadow-none text-black"
                        :required="local_step.type === 'V'"
                        :class="[{ 'is-invalid': submitted && errors?.min_label }]" />
                    <!-- ERRORS -->
                    <div class="form-messages mt-2 px-1" v-if="errors?.min_label ?? false">
                        <p v-for="error in errors.min_label" :key="error" class="small text-danger lh-1 m-0 mt-1">
                            {{ error }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="col-12"></div>
            <!-- MAX -->
            <div class="col-6 col-sm-4 col-md-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('feature.surveys.max_value') }}*
                    </label>
                    <input v-model="local_step.max_value" class="form-control shadow-none text-black"
                        :required="local_step.type === 'V'" type="number"
                        :class="[{ 'is-invalid': submitted && errors?.max_value }]" />
                    <!-- ERRORS -->
                    <div class="form-messages mt-2 px-1" v-if="errors?.max_value ?? false">
                        <p v-for="error in errors.max_value" :key="error" class="small text-danger lh-1 m-0 mt-1">
                            {{ error }}
                        </p>
                    </div>
                </div>
            </div>
            <!-- MAX LABEL -->
            <div class="col-sm-8 col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('feature.surveys.max_label') }}*
                    </label>
                    <input v-model="local_step.max_label" class="form-control shadow-none text-black"
                        :required="local_step.type === 'V'"
                        :class="[{ 'is-invalid': submitted && errors?.max_label }]" />
                    <!-- ERRORS -->
                    <div class="form-messages mt-2 px-1" v-if="errors?.max_label ?? false">
                        <p v-for="error in errors.max_label" :key="error" class="small text-danger lh-1 m-0 mt-1">
                            {{ error }}
                        </p>
                    </div>
                </div>
            </div>
            <div class="col-12"></div>
            <!-- STEP INCREMENT -->
            <div class="col-6 col-sm-4 col-md-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('feature.surveys.step_increment') }}*
                    </label>
                    <input v-model="local_step.step_increment" class="form-control shadow-none text-black"
                        :required="local_step.type === 'V'" type="number"
                        :class="[{ 'is-invalid': submitted && errors?.step_increment }]" />
                    <!-- ERRORS -->
                    <div class="form-messages mt-2 px-1" v-if="errors?.step_increment ?? false">
                        <p v-for="error in errors.step_increment" :key="error" class="small text-danger lh-1 m-0 mt-1">
                            {{ error }}
                        </p>
                    </div>
                </div>
            </div>
        </template>
        <!-- REQUIRED -->
        <div class="col-12">
            <div class="form-check form-group form-switch mb-15 mb-sm-20 mb-md-25">
                <input class="form-check-input shadow-none me-8" type="checkbox" role="switch"
                    v-model="local_step.required" id="required" :true-value="1" :false-value="0">
                <label class="form-check-label fw-medium position-relative top-1" for="required">
                    {{ $t('feature.surveys.required') }}
                </label>
            </div>
        </div>
        <!-- INCLUDE NA OPTION -->
        <div class="col-12">
            <div class="form-check form-group form-switch mb-15 mb-sm-20 mb-md-25">
                <input class="form-check-input shadow-none me-8" type="checkbox" role="switch"
                    v-model="local_step.include_na_option" id="include_na_option" :true-value="1" :false-value="0">
                <label class="form-check-label fw-medium position-relative top-1" for="include_na_option">
                    {{ $t('feature.surveys.include_na_option') }}
                </label>
            </div>
        </div>
        <!-- CONDITIONS -->
        <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('feature.surveys.conditions') }}
                </label>
                <code>
            <input v-model="local_step.conditions" class="form-control shadow-none text-black"
                :class="[{ 'is-invalid': submitted && errors?.conditions }]" />
        </code>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.conditions ?? false">
                    <p v-for="error in errors.conditions" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}
                    </p>
                </div>
            </div>
        </div>
        <div class="col-12"></div>
        <!-- DISPLAY IN CHART -->
        <div class="col-12">
            <div class="form-check form-group form-switch mb-15 mb-sm-20 mb-md-25">
                <input class="form-check-input shadow-none me-8" type="checkbox" role="switch"
                    v-model="local_step.display_in_chart" id="display_in_chart" :true-value="1" :false-value="0">
                <label class="form-check-label fw-medium position-relative top-1" for="display_in_chart">
                    {{ $t('feature.surveys.display_in_chart') }}
                </label>
            </div>
        </div>
        <template v-if="local_step.display_in_chart">
            <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('feature.surveys.chart_type') }}*
                    </label>
                    <div>
                        <select class="form-select shadow-none text-black fs-md-15" v-model="local_step.chart_type"
                            :required="local_step.display_in_chart"
                            :class="[{ 'is-invalid': submitted && errors?.chart_type }]">
                            <option v-for="key in chart_types" :value="key" :key="key">
                                {{ $t(`feature.surveys.$chart_type.${key}`) }}</option>
                        </select>
                        <!-- ERRORS -->
                        <div class="form-messages mt-2 px-1" v-if="errors?.value_representation_type ?? false">
                            <p v-for="error in errors.value_representation_type" :key="error"
                                class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12"></div>
        </template>
    </div>
</template>

<script lang="ts">
import QUILL_TOOLBAR from '@/utils/const/quill_config';
import { SurveyStep, SurveyStepType, SurveyStepRepresentationType, SurveyStepChartType } from '@/utils/models/survey.model';
import { PropType } from 'vue';
import { defineComponent } from 'vue';

export default defineComponent({
    props: {
        step: {
            type: Object as PropType<SurveyStep>,
            required: true,
        },
        submitted: {
            type: Boolean,
            requred: true,
        },
        errors: {
            type: Object as PropType<any>,
        },
        update: {
            type: Boolean,
            required: true,
        },
        lang: {
            type: String,
            default: process.env.VUE_APP_LANG as string,
            required: true
        }
    },
    watch: {
        lang: function (locale) {
            this.normalize_step();
        },
        step: function (value) {
            this.local_step = value;
            this.normalize_step();
        },
        'local_step.display_in_chart': function (value) {
            delete this.local_step.chart_type;
        },
        'local_step.type': function (value) {
            delete this.local_step.min_value;
            delete this.local_step.max_value;
            delete this.local_step.min_label;
            delete this.local_step.max_label;
            delete this.local_step.step_increment;
            delete this.local_step.value_representation_type;
        } 
    },
    data() {
        let step = { ...this.step };
        step.title ??= {};
        step.text ??= {};
        step.body ??= {};
        return {
            local_step: { ...step } as SurveyStep,
            raw_edit: false,
            changes: 0,
            toolbar: QUILL_TOOLBAR
        }
    },
    mounted() {
        this.normalize_step();
    },
    computed: {
        typologies(): SurveyStepType {
            return Object(SurveyStepType);
        },
        value_representation_types(): SurveyStepRepresentationType[] {
            return ['integer', 'percentage', 'other'];
        },
        chart_types(): SurveyStepChartType[] {
            return ['line', 'bar', 'body_map'];
        }
    },
    methods: {
        resetBlock() {
            this.local_step.title = {}
            this.local_step.text = {}
            this.local_step.body = {}
            this.normalize_step();
            this.raw_edit = false;
            this.changes++;
        },
        toggleRawEdit() {
            this.raw_edit = !this.raw_edit;
        },
        normalize_step() {
            this.local_step.title ??= {};
            this.local_step.text ??= {};
            this.local_step.body ??= {};
            if (!this.local_step.title?.hasOwnProperty(this.lang)) {
                this.local_step.title[this.lang] = "";
            }
            if (!this.local_step.text?.hasOwnProperty(this.lang)) {
                this.local_step.text[this.lang] = "";
            }
            if (!this.local_step.body?.hasOwnProperty(this.lang)) {
                this.local_step.body[this.lang] = "";
            }
        }
    },
    name: "StepForm",
});
</script>